import React from 'react';
import appStyles from '../App.module.css';
import styles from './Pages.module.css';
import Wall from '../components/Wall';
import Tile from '../components/Tile';
import MailCta from '../components/MailCta';
import { upcomingMeetups, shortDate, gcalUrl } from '../meetup';

/**
 * /meetup — the standing meetup page.
 * Cadence: every other Thursday, 6–8 PM. The dates are computed from the
 * cadence in src/meetup.ts at render, so nothing here changes between
 * meetups; the calendar links are recurring series. Edit src/meetup.ts only
 * if the cadence shifts or a night is skipped; the venue lives in the copy.
 */

const DIRECTIONS_URL =
  'https://www.google.com/maps/dir/?api=1&destination=900+E+Clarendon+Dr+Dallas+TX+75203';

const Meetup: React.FC = () => {
  const [next, following] = upcomingMeetups(2);
  const NEXT_DATE = shortDate(next);
  const FOLLOWING_DATE = shortDate(following);
  const GCAL_URL = gcalUrl(next);
  return (
  <Wall>
    {/* Page hero — span 4, surface */}
    <Tile span={4} tone="surface">
      <div className={styles.kicker}>◆ Every other Thursday. Show us what you&rsquo;ve got.</div>
      <h1 className={styles.slab}>Next<br />Meetup</h1>
      <p className={styles.lead}>
        <b>This one has a job to do: bring something you&rsquo;re working on.</b>{' '}
        An app, a side project, a sketchbook, a half-broken robot. No stage, no
        slides — just a table, a drink, and the thing you&rsquo;ve been building.
      </p>
    </Tile>

    {/* 01 / When & Where — span 2, ink */}
    <Tile span={2} tone="ink" label="01 / When & Where" glyph="◆">
      <div className={styles.bigDate}>
        {NEXT_DATE}<br />6&ndash;8 PM
      </div>
      <div className={styles.sub}>
        Every other Thursday &middot; next up {FOLLOWING_DATE}<br />
        Lola&rsquo;s Cantina at East Dock<br />900 E. Clarendon Dr, Dallas, TX 75203
      </div>
      <div className={styles.linkRow}>
        <a className={styles.linkOchre} href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer">
          ◆ Get directions
        </a>
        <a className={styles.linkOchre} href={GCAL_URL} target="_blank" rel="noopener noreferrer">
          + Google Calendar
        </a>
        <a className={styles.linkOchre} href="/oakcliff-tech-meetup.ics" download>
          + Apple / Outlook (.ics, recurring)
        </a>
      </div>
    </Tile>

    {/* 02 / The Format — span 3 */}
    <Tile span={3} label="02 / The Format" glyph="▸">
      <div className={appStyles.list}>
        <div className={appStyles.listItem}>
          <b>Show &amp; tell mixer</b>
          <span>Tables, drinks, and whatever your neighbors brought. Short welcome at 6:30 — the rest is unstructured on purpose.</span>
        </div>
        <div className={appStyles.listItem}>
          <b>Every other Thursday</b>
          <span>Same table, same time, every two weeks. Miss one, catch the next &mdash; add the series to your calendar and stop thinking about it.</span>
        </div>
        <div className={appStyles.listItem}>
          <b>First time? Good.</b>
          <span>No badges, no pitch, no sponsor decks. Walk in, grab a drink, find the tables with projects on them.</span>
        </div>
        <div className={appStyles.listItem}>
          <b>Not building anything?</b>
          <span>Come anyway. Lurkers, learners, and the tech-curious are all neighbors here.</span>
        </div>
      </div>
    </Tile>

    {/* 03 / Bring — span 3, olive */}
    <Tile span={3} tone="olive" label="03 / Bring" glyph="✦">
      <p className={styles.creamCopy}>
        <b>The thing you&rsquo;re working on</b> &middot; one person who builds
        things &middot; yourself. If it fits in a bag, it belongs on the table.
      </p>
    </Tile>

    {/* 04 / RSVP — span 3, ochre */}
    <Tile span={3} tone="ochre" label="04 / RSVP" glyph="◆">
      <MailCta
        label="I'm coming"
        subject={`RSVP: Oak Cliff Tech Meetup, ${NEXT_DATE}`}
        body={'Name:\n'}
        hint="A headcount helps us hold the tables. No spam, ever."
      />
    </Tile>

    {/* 05 / Spread It — span 3, brick */}
    <Tile span={3} tone="brick" label="05 / Spread It" glyph="▸">
      <p className={styles.creamCopy}>
        <b>Every yes fills the room.</b> Forward this to one person in the
        neighborhood who builds things — or just send them the link.
      </p>
      <div className={styles.linkRow}>
        <a className={styles.linkCream} href="/">
          ◂ Back to oakcliff.tech
        </a>
      </div>
    </Tile>
  </Wall>
  );
};

export default Meetup;
